import { useEffect, useRef } from 'react';

import Pusher, { Channel } from 'pusher-js';

interface PusherError {
  message?: string;
  code?: string;
  data?: unknown;
}

interface UsePusherOptions<T> {
  channelName: string | null;
  eventName: string;
  onEvent: (data: T) => void;
  enabled?: boolean;
}

let pusherInstance: Pusher | null = null;

export const getPusherInstance = (): Pusher => {
  if (!pusherInstance) {
    const PUSHER_APP_KEY = import.meta.env.VITE_PUSHER_APP_KEY as string;
    pusherInstance = new Pusher(PUSHER_APP_KEY, {
      cluster: 'sa1',
    });
  }
  return pusherInstance;
};

export const disconnectPusher = (): void => {
  if (pusherInstance) {
    pusherInstance.disconnect();
    pusherInstance = null;
  }
};

const usePusher = <T = unknown>({ channelName, eventName, onEvent, enabled = true }: UsePusherOptions<T>): void => {
  const channelRef = useRef<Channel | null>(null);
  const currentChannelNameRef = useRef<string | null>(null);
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!enabled || !channelName) {
      if (channelRef.current && currentChannelNameRef.current) {
        const pusher = getPusherInstance();
        pusher.unsubscribe(currentChannelNameRef.current);
        channelRef.current = null;
        currentChannelNameRef.current = null;
      }
      return;
    }

    const pusher = getPusherInstance();

    if (!channelRef.current || currentChannelNameRef.current !== channelName) {
      if (channelRef.current && currentChannelNameRef.current) {
        pusher.unsubscribe(currentChannelNameRef.current);
        channelRef.current = null;
      }

      channelRef.current = pusher.subscribe(channelName);
      currentChannelNameRef.current = channelName;
      const channel = channelRef.current;

      channel.bind('pusher:subscription_error', (error: PusherError) => {
        // eslint-disable-next-line no-console
        console.error('Error subscribing to channel:', {
          channel: channelName,
          error,
          message: error?.message,
        });
      });
    }

    channelRef.current.bind(eventName, (data: T) => {
      onEventRef.current(data);
    });

    // eslint-disable-next-line consistent-return
    return () => {
      if (channelRef.current) {
        channelRef.current.unbind(eventName);
      }
    };
  }, [channelName, eventName, enabled]);

  useEffect(
    () => () => {
      if (channelRef.current && currentChannelNameRef.current) {
        const pusher = getPusherInstance();
        pusher.unsubscribe(currentChannelNameRef.current);
        channelRef.current = null;
        currentChannelNameRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (!enabled) return;

    const pusher = getPusherInstance();
    pusher.connection.bind('error', (err: PusherError) => {
      // eslint-disable-next-line no-console
      console.error('Pusher connection error:', {
        error: err,
        message: err?.message,
        code: err?.code,
      });
    });

    // eslint-disable-next-line consistent-return
    return () => {
      pusher.connection.unbind('error');
    };
  }, [enabled]);
};

export default usePusher;
